import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Package, Plus, Trash2, Save, Copy, FileStack, Activity as ActivityIcon, ListChecks, ClipboardCopy, LineChart as LineChartIcon, ChevronDown, ChevronRight, Layers, Share2, Link2, Sparkles, Shuffle, CalendarRange, Send } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import PackageMonthlyBreakdown from "@/components/packages/PackageMonthlyBreakdown";
import ActivityTab from "@/components/ActivityTab";
import { PKG_STATUS_TONES, CATEGORIES, FREQ_LABELS, fmtMoney } from "@/pages/app/Packages";
import AuditFooter from "@/components/AuditFooter";
import AuditTrailPanel from "@/components/AuditTrailPanel";
import PackageLinesTable, { sumSkillHours } from "@/components/packages/PackageLinesTable";
import ActivityMasterCombobox from "@/components/packages/ActivityMasterCombobox";
import PackageModeToggle from "@/components/packages/PackageModeToggle";
import ActivityPresetsDialog from "@/components/packages/ActivityPresetsDialog";
import BulkReassignDialog from "@/components/packages/BulkReassignDialog";
import PackageCalendarSyncDialog from "@/components/packages/PackageCalendarSyncDialog";
import { ChannelIcons, channelsToPlainText, getChannel } from "@/components/packages/channels";
import SmartRichText, { SmartRichTextDisplay } from "@/components/SmartRichText";
import usePackageMode from "@/lib/usePackageMode";

export default function PackageDetail() {
  const { pid } = useParams();
  const nav = useNavigate();
  const { activeWorkspaceId } = useAuth();
  const [pkg, setPkg] = useState(null);
  const [lines, setLines] = useState([]);
  const [sections, setSections] = useState([]);           // R78
  const [notesHtml, setNotesHtml] = useState("");         // R78
  const [meta, setMeta] = useState({ discount_pct: 0, start_date: "", end_date: "", notes: "", project_id: "", auto_generate: false, naming_pattern: "" });
  const [activityTypes, setActivityTypes] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [projects, setProjects] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [overviewCollapsed, setOverviewCollapsed] = useState(() => { // R78
    try { return localStorage.getItem("co_pkg_overview_collapsed") === "1"; } catch { return false; }
  });
  const [presetsOpen, setPresetsOpen] = useState(false);  // R78
  // R99 · Bulk reassign a designation across the whole package.
  const [bulkReassignOpen, setBulkReassignOpen] = useState(false);
  // Feb 2026 · Content Calendar sync
  const [syncOpen, setSyncOpen] = useState(false);
  const [tolerancePct, setTolerancePct] = useState(50);   // R79
  const [tolEditing, setTolEditing] = useState(false);
  const [tolDraft, setTolDraft] = useState("50");
  // R80 · Collapsible "Package details" section (start/end/project/etc.)
  const [detailsCollapsed, setDetailsCollapsed] = useState(() => {
    try { return localStorage.getItem("co_pkg_details_collapsed") === "1"; } catch { return false; }
  });
  const toggleDetails = () => {
    setDetailsCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem("co_pkg_details_collapsed", next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };
  // R80 · Approval + share
  const [approval, setApproval] = useState({
    approval_status: "draft", approval_date: "", approver_name: "", approver_type: "internal",
  });
  const [shareToken, setShareToken] = useState(null);
  const [brandName, setBrandName] = useState("");
  const [clientName, setClientName] = useState("");
  // R77 · Mode hook MUST be called before any early return (React rules of hooks).
  const modeCtl = usePackageMode();

  const load = async () => {
    const r = await api.get(`/packages/${pid}`);
    setPkg(r.data);
    setLines(r.data.lines || []);
    setSections(r.data.sections || []);
    setNotesHtml(r.data.notes_html || "");
    setMeta({
      discount_pct: r.data.discount_pct || 0, start_date: r.data.start_date || "",
      end_date: r.data.end_date || "", notes: r.data.notes || "", project_id: r.data.project_id || "",
      auto_generate: !!r.data.auto_generate, naming_pattern: r.data.naming_pattern || "",
    });
    setApproval({
      approval_status: r.data.approval_status || "draft",
      approval_date: r.data.approval_date || "",
      approver_name: r.data.approver_name || "",
      approver_type: r.data.approver_type || "internal",
    });
    setShareToken(r.data.share_token || null);
    setBrandName(r.data.brand_name || "");
    setClientName(r.data.client_name || "");
    setDirty(false);
  };
  useEffect(() => { load().catch((e) => toast.error(apiErrorMessage(e, "Failed to load"))); /* eslint-disable-next-line */ }, [pid]);
  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/activity-types`).then((r) => setActivityTypes(r.data || [])).catch(() => {});
    Promise.all([
      api.get(`/workspaces/${activeWorkspaceId}/designations`),
      api.get(`/workspaces/${activeWorkspaceId}/designation-rates`),
    ]).then(([d, r]) => setDesignations(Array.from(new Set([...(d.data || []), ...Object.keys(r.data?.rates || {})])).sort())).catch(() => {});
    api.get(`/workspaces/${activeWorkspaceId}/projects`).then((r) => setProjects(r.data || [])).catch(() => {});
    api.get(`/workspaces/${activeWorkspaceId}/package-settings`)
      .then((r) => {
        const v = Number(r.data?.effort_tolerance_pct);
        if (v > 0) { setTolerancePct(v); setTolDraft(String(v)); }
        setCustomCategories(Array.isArray(r.data?.custom_categories) ? r.data.custom_categories : []);
      }).catch(() => {});
    // Iter · Feb 2026 — Merge in the workspace-editable content_categories
    // added via Manage Categories dialog. Older packages used the legacy
    // package_settings.custom_categories list; the two lists dedupe by name.
    api.get(`/workspaces/${activeWorkspaceId}/content-categories`)
      .then((r) => {
        const names = (r.data || [])
          .filter((c) => c.active !== false)
          .map((c) => (c.name || "").trim())
          .filter(Boolean);
        setContentCategories(names);
      }).catch(() => setContentCategories([]));
    // R84 · workspace members for the skill-breakdown default-assignee picker.
    api.get(`/workspaces/${activeWorkspaceId}/members`).then((r) => setMembers(r.data || [])).catch(() => {});
  }, [activeWorkspaceId]);

  const [members, setMembers] = useState([]);                     // R84
  const [customCategories, setCustomCategories] = useState([]);   // R83
  const [contentCategories, setContentCategories] = useState([]); // Iter · Feb 2026
  const mergedCategories = useMemo(() => (
    Array.from(new Set([...(customCategories || []), ...(contentCategories || [])]))
  ), [customCategories, contentCategories]);
  const [previewGrouped, setPreviewGrouped] = useState(() => {    // R83
    try { return localStorage.getItem("co_pkg_preview_flat") !== "1"; } catch { return true; }
  });
  const setPreviewGroupedPersist = (v) => {
    setPreviewGrouped(v);
    try { localStorage.setItem("co_pkg_preview_flat", v ? "0" : "1"); } catch { /* ignore */ }
  };
  const [titleEditing, setTitleEditing] = useState(false);        // R83 · rename
  const [titleDraft, setTitleDraft] = useState("");

  const saveTolerance = async (val) => {
    const num = Math.max(5, Math.min(500, Math.round(Number(val))));
    if (!Number.isFinite(num) || num === tolerancePct) { setTolEditing(false); setTolDraft(String(tolerancePct)); return; }
    try {
      await api.put(`/workspaces/${activeWorkspaceId}/package-settings`, { effort_tolerance_pct: num });
      setTolerancePct(num); setTolDraft(String(num));
      toast.success(`Effort tolerance set to ±${num}%`);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to save tolerance"));
      setTolDraft(String(tolerancePct));
    } finally { setTolEditing(false); }
  };

  const toggleOverview = () => {
    setOverviewCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem("co_pkg_overview_collapsed", next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  // R80 · Hooks that MUST be declared before any early return.
  const [shareBusy, setShareBusy] = useState(false);

  // R268vvv · Feb 2026 — Backfill prompt state (also MUST be above early return).
  const [backfill, setBackfill] = useState(null); // { line, next, prev } or null
  // Feb 2026 · Skill-hours → tasks backfill dialog state. Opens when a
  // PM edits a skill-breakdown row's hours and clicks the ↻ chip.
  const [skillBackfill, setSkillBackfill] = useState(null);
  // { line_id, designation, hours } or null
  const _defaultMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
  // Feb 2026 · When the backfill dialog opens we default the range to
  // "current month → +11 future months" so PMs instantly see every
  // in-flight and planned deliverable the ownership change should
  // apply to, not just the current month. The dialog's inline date
  // inputs still let them narrow or extend the window.
  const _defaultFutureMonth = () => {
    const d = new Date();
    d.setMonth(d.getMonth() + 11);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  };
  const [backfillFrom, setBackfillFrom] = useState(_defaultMonth());
  const [backfillTo,   setBackfillTo]   = useState(_defaultFutureMonth());
  const [backfillBusy, setBackfillBusy] = useState(false);
  // R268bbbb · Feb 2026 — Live preview: list of impacted deliverables
  // + set of ids the PM has ticked OUT of the update. Fetched every
  // time the month range changes so the preview stays in step with
  // the pickers.
  const [backfillPreview, setBackfillPreview] = useState({ items: [], loading: false });
  const [backfillExcluded, setBackfillExcluded] = useState(new Set());

  // R268bbbb · Feb 2026 — Preview fetcher. Runs on dialog open and on
  // every month-range change so the checkbox list always reflects the
  // current window. MUST be declared above the `if (!pkg) return`
  // early return below so hook order stays stable across renders.
  useEffect(() => {
    if (!backfill || !pid || !backfillFrom || !backfillTo || backfillFrom > backfillTo) {
      setBackfillPreview({ items: [], loading: false });
      return;
    }
    let alive = true;
    setBackfillPreview((p) => ({ items: p.items || [], loading: true }));
    api.get(`/workspaces/${activeWorkspaceId}/packages/${pid}/primary-assignee-backfill/preview`, {
      params: { line_id: backfill.line.id, from_month: backfillFrom, to_month: backfillTo },
    }).then((r) => {
      if (!alive) return;
      const items = r.data?.items || [];
      setBackfillPreview({ items, loading: false });
      setBackfillExcluded(new Set());
    }).catch(() => { if (alive) setBackfillPreview({ items: [], loading: false }); });
    return () => { alive = false; };
  }, [backfill, backfillFrom, backfillTo, pid, activeWorkspaceId]);
  const dragSuggestion = useMemo(() => {
    if (!lines?.length) return null;
    const buckets = new Map();
    lines.forEach((l) => {
      const key = `${l.section_id || "_none"}::${l.category || "other"}`;
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(l);
    });
    for (const [key, arr] of buckets) {
      if (arr.length < 3) continue;
      const [sid, cat] = key.split("::");
      if (sid !== "_none") continue;
      const existingSameName = (sections || []).some(
        (s) => (s.name || "").toLowerCase() === cat.toLowerCase()
      );
      if (existingSameName) continue;
      return { cat, ids: arr.map((l) => l.id), count: arr.length };
    }
    return null;
  }, [lines, sections]);

  if (!pkg) return <div className="px-10 py-10 text-sm text-muted-foreground">Loading…</div>;

  const markDirty = () => setDirty(true);

  // R79 · Autosave only the reorder-y fields (sections + lines' section_id/
  // sort_order) after a drag so a mistimed refresh never loses drag work.
  // The heavy `Save changes` button still handles content edits.
  const autosaveOrder = async (nextLines, nextSections) => {
    try {
      await api.patch(`/packages/${pid}`, {
        lines: nextLines.map((l) => ({
          ...l,
          count: Number(l.count) || 1,
          rate: Number(l.rate) || 0,
          effort_hours: Number(l.effort_hours) || 0,
          channels: Array.isArray(l.channels) ? l.channels : [],
          skill_breakdown: (l.skill_breakdown || []).filter((s) => s.designation)
            .map((s) => ({ designation: s.designation, hours: Number(s.hours) || 0, assignee_id: s.assignee_id || null })),
        })),
        sections: nextSections,
      });
      // Silent success — nothing to toast, drag UX is already the feedback.
    } catch (e) {
      toast.error(apiErrorMessage(e, "Order autosave failed"));
    }
  };

  const updateLine = (i, patch) => { setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l))); markDirty(); };
  const removeLine = (i) => { setLines((ls) => ls.filter((_, j) => j !== i)); markDirty(); };

  // R268vvv · Feb 2026 — When the PM changes a line's primary assignee,
  // we stash the change here so the modal below can gather the month range
  // and call the backend endpoint. State hooks live above the early return.
  const handlePrimaryAssigneeChanged = (line, next, prev) => {
    // Nov 2026 · If the package hasn't been saved yet there's no pid
    // and no generated deliverables to backfill — give the PM a soft
    // hint so they know the new owner is still being captured.
    if (!pid) {
      toast.info("New owner saved on this line — it will apply to every deliverable generated from this package.");
      return;
    }
    setBackfill({ line, next, prev });
    setBackfillFrom(_defaultMonth());
    setBackfillTo(_defaultFutureMonth());
  };
  const runBackfill = async () => {
    if (!backfill || !pid) return;
    setBackfillBusy(true);
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/packages/${pid}/primary-assignee-backfill`,
        {
          line_id: backfill.line.id,
          primary_assignee_user_id: backfill.next,
          from_month: backfillFrom,
          to_month: backfillTo,
          excluded_item_ids: [...backfillExcluded],
        },
      );
      const skipped = data.excluded_count ? ` · ${data.excluded_count} kept` : "";
      toast.success(`Updated ${data.modified} deliverable${data.modified === 1 ? "" : "s"}${skipped}`);
      setBackfill(null);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Backfill failed"));
    } finally { setBackfillBusy(false); }
  };

  const reorderLines = (next) => {
    setLines(next); markDirty();
    // Fire-and-forget autosave with the latest sections snapshot.
    autosaveOrder(next, sections);
  };
  const updateSections = (next) => {
    setSections(next); markDirty();
    autosaveOrder(lines, next);
  };
  const addSection = () => {
    const name = window.prompt("Section name?");
    if (!name || !name.trim()) return;
    const now = Date.now();
    setSections((s) => [...s, {
      id: `s_${now}_${Math.random().toString(36).slice(2, 8)}`,
      name: name.trim(),
      order: (s.length + 1) * 10,
    }]);
    markDirty();
  };
  const addFromMaster = (aid) => {
    const a = activityTypes.find((x) => x.id === aid);
    if (!a) return;
    setLines((ls) => [...ls, {
      activity_type_id: a.id, name: a.name, category: a.category, is_external: a.is_external !== false,
      frequency: "monthly", count: 1, effort_hours: a.default_effort_hours || 0,
      skill_breakdown: a.skill_breakdown || [], rate: a.default_rate || 0, notes: "",
      section_id: null, sort_order: (ls.length + 1) * 10,
      channels: a.default_channels || [],
    }]);
    markDirty();
  };
  const addCustom = () => {
    setLines((ls) => [...ls, {
      name: "New line", category: "other", is_external: true, frequency: "monthly",
      count: 1, effort_hours: 0, skill_breakdown: [], rate: 0, notes: "",
      section_id: null, sort_order: (ls.length + 1) * 10,
      channels: [],
    }]);
    markDirty();
  };
  const applyPreset = (nextLines, nextSections) => {
    setLines(nextLines);
    setSections(nextSections);
    markDirty();
  };

  // R80 · Drag-to-Group accept + Share link management (functions only; state
  // was declared above the early return).
  const acceptDragSuggestion = () => {
    if (!dragSuggestion) return;
    const now = Date.now();
    const newSectionId = `s_${now}_${Math.random().toString(36).slice(2, 8)}`;
    const nextSections = [
      ...sections,
      { id: newSectionId, name: dragSuggestion.cat.charAt(0).toUpperCase() + dragSuggestion.cat.slice(1), order: (sections.length + 1) * 10 },
    ];
    const idSet = new Set(dragSuggestion.ids);
    const nextLines = lines.map((l) => idSet.has(l.id) ? { ...l, section_id: newSectionId } : l);
    setSections(nextSections);
    setLines(nextLines);
    markDirty();
    autosaveOrder(nextLines, nextSections);
    toast.success(`Created section "${dragSuggestion.cat}" with ${dragSuggestion.count} items`);
  };

  const shareUrl = shareToken ? `${window.location.origin}/pkg/view/${shareToken}` : null;
  const generateShareLink = async () => {
    setShareBusy(true);
    try {
      const r = await api.post(`/packages/${pid}/share`);
      setShareToken(r.data?.token || null);
      const url = `${window.location.origin}/pkg/view/${r.data?.token}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Shareable link copied — paste into email or chat");
      } catch { toast.success("Link ready. Copy from the input above."); }
    } catch (e) { toast.error(apiErrorMessage(e, "Share failed")); }
    finally { setShareBusy(false); }
  };
  const revokeShareLink = async () => {
    if (!window.confirm("Revoke this share link? Anyone with the URL will lose access.")) return;
    try {
      await api.delete(`/packages/${pid}/share`);
      setShareToken(null);
      toast.success("Share link revoked");
    } catch (e) { toast.error(apiErrorMessage(e, "Revoke failed")); }
  };
  const copyShareLink = async () => {
    if (!shareUrl) return;
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); }
    catch { toast.error("Copy blocked"); }
  };

  const saveAll = async () => {
    setBusy(true);
    try {
      const res = await api.patch(`/packages/${pid}`, {
        lines: lines.map((l) => ({ ...l, count: Number(l.count) || 1, rate: Number(l.rate) || 0, effort_hours: Number(l.effort_hours) || 0,
          channels: Array.isArray(l.channels) ? l.channels : [],
          skill_breakdown: (l.skill_breakdown || []).filter((s) => s.designation).map((s) => ({ designation: s.designation, hours: Number(s.hours) || 0, assignee_id: s.assignee_id || null })) })),
        sections,
        notes_html: notesHtml || null,
        discount_pct: Number(meta.discount_pct) || 0,
        start_date: meta.start_date, end_date: meta.end_date || null,
        notes: meta.notes || null, project_id: meta.project_id || null,
        auto_generate: !!meta.auto_generate, naming_pattern: meta.naming_pattern || null,
        approval_status: approval.approval_status || "draft",
        approval_date: approval.approval_date || null,
        approver_name: approval.approver_name || null,
        approver_type: approval.approver_type || null,
      });
      // Iter · Feb 2026 — Surface cascade report so users see the
      // deliverables were re-tagged automatically.
      const cascades = res?.data?._category_cascades || [];
      const items = res?.data?._cascade_report?.content_items || 0;
      if (cascades.length) {
        toast.success(`Package saved · auto-updated ${items} deliverable${items === 1 ? "" : "s"} across ${cascades.length} line${cascades.length === 1 ? "" : "s"}`);
      } else {
        toast.success("Package saved");
      }
      // R85 · If the save changed any skill assignees AND matched future
      // open tasks, prompt to bulk-reassign them in one shot.
      // R86 · With user confirmation, auto-apply on save + surface an
      // "Undo" action so leaders don't have to hunt for the toast button.
      const pending = res?.data?._pending_reassignments || [];
      if (pending.length) {
        const total = pending.reduce((s, p) => s + (p.task_count || 0), 0);
        const preview = pending.slice(0, 3).map((p) => `${p.designation}: ${p.task_count}`).join(" · ");
        const ok = window.confirm(
          `${total} open future task${total === 1 ? "" : "s"} match this assignee change.\n\n${preview}\n\nApply now?`,
        );
        if (ok) {
          try {
            const items = pending.map((p) => ({
              line_id: p.line_id,
              designation: p.designation,
              prev_assignee_id: p.prev_assignee_id || null,
              next_assignee_id: p.next_assignee_id || null,
            }));
            const r = await api.post(`/packages/${pid}/apply-reassignments`, { items });
            const updated = r.data?.updated || 0;
            toast.success(`Reassigned ${updated} task${updated === 1 ? "" : "s"}`, {
              duration: 12000,
              action: updated > 0 ? {
                label: "Undo",
                onClick: async () => {
                  try {
                    // Reverse the mapping: prev ↔ next.
                    const undoItems = items.map((it) => ({
                      line_id: it.line_id,
                      designation: it.designation,
                      prev_assignee_id: it.next_assignee_id || null,
                      next_assignee_id: it.prev_assignee_id || null,
                    }));
                    const u = await api.post(`/packages/${pid}/apply-reassignments`, { items: undoItems });
                    toast.success(`Undone — ${u.data?.updated || 0} task${(u.data?.updated || 0) === 1 ? "" : "s"} reverted`);
                  } catch (e) { toast.error(apiErrorMessage(e, "Undo failed")); }
                },
              } : undefined,
            });
          } catch (e) { toast.error(apiErrorMessage(e, "Reassignment failed")); }
        }
      }
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  const setStatus = async (status) => {
    const comment = window.prompt(`Change status to "${status}" — add a comment (optional):`) ?? "";
    try {
      await api.post(`/packages/${pid}/status`, { status, comment: comment || null });
      toast.success(`Status → ${status}`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Status change failed")); }
  };

  const duplicate = async () => {
    const r = await api.post(`/packages/${pid}/duplicate`);
    toast.success("Duplicated as draft");
    nav(`/app/packages/${r.data.id}`);
  };
  const saveTemplate = async () => {
    const name = window.prompt("Template name?", pkg.name);
    if (!name) return;
    await api.post(`/packages/${pid}/save-template`, { name });
    toast.success("Saved as template");
  };
  const del = async () => {
    if (!window.confirm(`Delete package "${pkg.name}"?`)) return;
    await api.delete(`/packages/${pid}`);
    toast.success("Deleted");
    nav("/app/packages");
  };

  const t = pkg.totals || {};
  // Financial-visibility flags surfaced by the backend so we can hide/redact
  // every price / cost / margin field on the client (belt-and-braces on top of
  // the server-side redactor in routes_packages._redact_package).
  const finance = pkg._finance_flags || {};
  const canRatesPerm = !!finance.can_view_rates;
  const canCostsPerm = !!finance.can_view_costs;
  const canMarginsPerm = !!finance.can_view_margins;
  const canFinancePerm = canRatesPerm || canCostsPerm || canMarginsPerm;

  // R77 · Delivery ⇄ Financial mode. `financialLensOn` gates the rendering;
  // the server still redacts fields the caller isn't allowed to see so
  // switching modes cannot leak data.
  const financialLensOn = modeCtl.mode === "financial" && canFinancePerm;
  const canRates = canRatesPerm && financialLensOn;
  const canCosts = canCostsPerm && financialLensOn;
  const canMargins = canMarginsPerm && financialLensOn;
  const showFinancials = financialLensOn;

  return (
    <>
      <div className="px-4 sm:px-6 md:px-10 pt-6">
        <Link to="/app/packages" className="text-sm label-eyebrow inline-flex items-center hover:text-primary" data-testid="pkg-back-link">← Back to packages</Link>
      </div>
      <PageHeader
        eyebrow={`Package · ${pkg.brand_name || ""}`}
        title={
          titleEditing ? (
            <input
              type="text"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={async () => {
                const nm = (titleDraft || "").trim();
                setTitleEditing(false);
                if (!nm || nm === pkg.name) return;
                try {
                  await api.patch(`/packages/${pid}`, { name: nm });
                  toast.success("Package renamed");
                  await load();
                } catch (e) { toast.error(apiErrorMessage(e, "Rename failed")); }
              }}
              onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") { setTitleDraft(pkg.name || ""); setTitleEditing(false); } }}
              autoFocus
              className="text-3xl font-bold bg-transparent border-b border-primary outline-none w-full"
              data-testid="pkg-title-input"
            />
          ) : (
            <button type="button" onClick={() => { setTitleDraft(pkg.name || ""); setTitleEditing(true); }}
                    className="text-left hover:opacity-70 focus:outline-none" title="Click to rename" data-testid="pkg-title-edit-btn">
              {pkg.name}
            </button>
          )
        }
        description={`${pkg.start_date} → ${pkg.end_date || "no end date"} · ${pkg.currency}`}
        testid="pkg-detail-header"
        actions={
          <div className="flex gap-2 items-center flex-wrap">
            <PackageModeToggle
              mode={modeCtl.mode}
              setMode={modeCtl.setMode}
              canToggle={modeCtl.canToggle && canFinancePerm}
              testid="pkg-detail-mode-toggle"
            />
            <Select value={pkg.status} onValueChange={setStatus}>
              <SelectTrigger className={`h-9 w-32 rounded-none ${PKG_STATUS_TONES[pkg.status] || ""}`} data-testid="pkg-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.keys(PKG_STATUS_TONES).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Button variant="outline" className="rounded-none" onClick={duplicate} data-testid="pkg-duplicate-btn"><Copy className="w-3.5 h-3.5 mr-1.5" /> Duplicate</Button>
            <Button variant="outline" className="rounded-none" onClick={saveTemplate} data-testid="pkg-save-template-btn"><FileStack className="w-3.5 h-3.5 mr-1.5" /> Save as template</Button>
            <Button variant="destructive" className="rounded-none" onClick={del} data-testid="pkg-delete-btn"><Trash2 className="w-3.5 h-3.5" /></Button>
            <AuditTrailPanel collection="packages" recordId={pid} testid="pkg-audit-trail" />
          </div>
        }
      />
      <div className="px-4 sm:px-6 md:px-10 pb-10 space-y-4">
        {/* R78 · Collapsible overview strip — click the pill to hide the four
             summary cards and free up real estate for the deliverables editor. */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="pkg-overview-toggle-wrap">
          <button
            type="button"
            onClick={toggleOverview}
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground border border-border px-2 py-1"
            data-testid="pkg-overview-toggle"
          >
            {overviewCollapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {overviewCollapsed ? "Show overview" : "Hide overview"}
          </button>
          <span className="text-[10px] text-muted-foreground">
            {lines.length} deliverables · {t.monthly_hours ?? 0}h/mo
          </span>
          {/* R79 · Effort warning tolerance ± % (admin-editable, per workspace). */}
          <div
            className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-muted-foreground border border-border px-2 py-1"
            title="Rows whose effort/u deviates more than this from the activity master baseline get a warning icon."
            data-testid="pkg-effort-tolerance"
          >
            <span>Effort tolerance ±</span>
            {tolEditing ? (
              <input
                type="number" min={5} max={500} step={5}
                value={tolDraft}
                onChange={(e) => setTolDraft(e.target.value)}
                onBlur={() => saveTolerance(tolDraft)}
                onKeyDown={(e) => { if (e.key === "Enter") saveTolerance(tolDraft); if (e.key === "Escape") { setTolEditing(false); setTolDraft(String(tolerancePct)); } }}
                autoFocus
                className="w-12 h-4 bg-transparent border-b border-primary text-[10px] text-center outline-none"
                data-testid="pkg-effort-tolerance-input"
              />
            ) : (
              <button type="button" onClick={() => setTolEditing(true)} className="font-bold hover:underline" data-testid="pkg-effort-tolerance-value">
                {tolerancePct}%
              </button>
            )}
          </div>
        </div>
        {!overviewCollapsed && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="pkg-totals">
            <TotalCard label="Deliverables" value={`${lines.length}`} testid="pkg-total-lines" />
            <TotalCard label="Monthly effort" value={`${t.monthly_hours ?? 0}h`} testid="pkg-total-hours" />
            <TotalCard label="Period" value={`${pkg.start_date || "—"} → ${pkg.end_date || "∞"}`} testid="pkg-total-period" />
            <TotalCard label="Status" value={pkg.status} testid="pkg-total-status" />
          </div>
        )}

        <Tabs defaultValue="lines" className="w-full">
          <TabsList className="rounded-none">
            <TabsTrigger value="lines" className="rounded-none" data-testid="pkg-tab-lines"><ListChecks className="w-3.5 h-3.5 mr-1.5" /> Delivery · {lines.length}</TabsTrigger>
            {showFinancials && (
              <TabsTrigger value="financials" className="rounded-none" data-testid="pkg-tab-financials"><LineChartIcon className="w-3.5 h-3.5 mr-1.5" /> Financials</TabsTrigger>
            )}
            <TabsTrigger value="activity" className="rounded-none" data-testid="pkg-tab-activity"><ActivityIcon className="w-3.5 h-3.5 mr-1.5" /> Activity & comments</TabsTrigger>
          </TabsList>

          <TabsContent value="lines" className="pt-4 space-y-4">
            {/* R80 · Package details — collapsible strip. Approval + share
                 fields live inside so PMs can toggle them out for a maximised
                 line-editing surface. */}
            <div className="border border-border">
              <button
                type="button"
                onClick={toggleDetails}
                className="w-full flex items-center gap-2 px-3 py-1.5 bg-muted/40 border-b border-border text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground"
                data-testid="pkg-details-toggle"
              >
                {detailsCollapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                <span>Package details</span>
                {approval.approval_status && approval.approval_status !== "draft" && (
                  <span
                    className={`ml-2 text-[9px] uppercase tracking-widest px-1.5 py-0.5 ${
                      approval.approval_status === "approved" ? "bg-emerald-100 text-emerald-800 border border-emerald-300" :
                      approval.approval_status === "rejected" ? "bg-rose-100 text-rose-800 border border-rose-300" :
                      "bg-amber-100 text-amber-800 border border-amber-300"
                    }`}
                    data-testid="pkg-details-approval-chip"
                  >{approval.approval_status}</span>
                )}
                <span className="ml-auto text-muted-foreground normal-case tracking-normal font-normal">
                  {meta.start_date || "—"} → {meta.end_date || "∞"}
                </span>
              </button>
              {!detailsCollapsed && (
                <div className="p-3 grid grid-cols-2 md:grid-cols-5 gap-3 items-end" data-testid="pkg-meta">
                  <div><Label className="label-eyebrow mb-1 inline-block">Start</Label>
                    <Input type="date" value={meta.start_date} onChange={(e) => { setMeta((s) => ({ ...s, start_date: e.target.value })); markDirty(); }} className="h-9 rounded-none" data-testid="pkg-meta-start" /></div>
                  <div><Label className="label-eyebrow mb-1 inline-block">End</Label>
                    <Input type="date" value={meta.end_date} onChange={(e) => { setMeta((s) => ({ ...s, end_date: e.target.value })); markDirty(); }} className="h-9 rounded-none" data-testid="pkg-meta-end" /></div>
                  {canRates && (
                    <div><Label className="label-eyebrow mb-1 inline-block">Discount %</Label>
                      <Input type="number" min={0} max={100} value={meta.discount_pct} onChange={(e) => { setMeta((s) => ({ ...s, discount_pct: e.target.value })); markDirty(); }} className="h-9 rounded-none" data-testid="pkg-meta-discount" /></div>
                  )}
                  <div><Label className="label-eyebrow mb-1 inline-block">Linked project</Label>
                    <Select value={meta.project_id || "_none"} onValueChange={(v) => { setMeta((s) => ({ ...s, project_id: v === "_none" ? "" : v })); markDirty(); }}>
                      <SelectTrigger className="h-9 rounded-none" data-testid="pkg-meta-project"><SelectValue placeholder="None" /></SelectTrigger>
                      <SelectContent><SelectItem value="_none">None</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label className="label-eyebrow mb-1 inline-block">Notes</Label>
                    <Input value={meta.notes} onChange={(e) => { setMeta((s) => ({ ...s, notes: e.target.value })); markDirty(); }} className="h-9 rounded-none" data-testid="pkg-meta-notes" /></div>
                  <div className="col-span-2 md:col-span-3">
                    <Label className="label-eyebrow mb-1 inline-block">Task naming pattern</Label>
                    <Input value={meta.naming_pattern} onChange={(e) => { setMeta((s) => ({ ...s, naming_pattern: e.target.value })); markDirty(); }}
                      placeholder="{seq}/{total} - {name} - {month_name} {year}" className="h-9 rounded-none font-mono text-xs" data-testid="pkg-meta-pattern" />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Tokens: {"{seq} {total} {name} {brand} {month_name} {month} {year} {category}"} → e.g. 1/4 - Reel - July 2026</p>
                  </div>
                  <div className="col-span-2 flex items-center gap-2 pb-1">
                    <input type="checkbox" id="pkg-autogen" checked={meta.auto_generate} onChange={(e) => { setMeta((s) => ({ ...s, auto_generate: e.target.checked })); markDirty(); }} data-testid="pkg-meta-autogen" />
                    <label htmlFor="pkg-autogen" className="text-sm cursor-pointer">Auto-generate content calendar <span className="text-xs text-muted-foreground">(daily cron; pre-builds next month from the 25th)</span></label>
                  </div>
                  {/* R80 · Approval fields */}
                  <div><Label className="label-eyebrow mb-1 inline-block">Approval status</Label>
                    <Select value={approval.approval_status} onValueChange={(v) => { setApproval((s) => ({ ...s, approval_status: v })); markDirty(); }}>
                      <SelectTrigger className="h-9 rounded-none" data-testid="pkg-approval-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select></div>
                  <div><Label className="label-eyebrow mb-1 inline-block">Approval date</Label>
                    <Input type="date" value={approval.approval_date} onChange={(e) => { setApproval((s) => ({ ...s, approval_date: e.target.value })); markDirty(); }} className="h-9 rounded-none" data-testid="pkg-approval-date" /></div>
                  <div><Label className="label-eyebrow mb-1 inline-block">Approver name</Label>
                    <Input value={approval.approver_name} onChange={(e) => { setApproval((s) => ({ ...s, approver_name: e.target.value })); markDirty(); }} placeholder="e.g. Ravi Menon" className="h-9 rounded-none" data-testid="pkg-approval-name" /></div>
                  <div><Label className="label-eyebrow mb-1 inline-block">Approver type</Label>
                    <Select value={approval.approver_type || "internal"} onValueChange={(v) => { setApproval((s) => ({ ...s, approver_type: v })); markDirty(); }}>
                      <SelectTrigger className="h-9 rounded-none" data-testid="pkg-approval-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="internal">Internal</SelectItem>
                        <SelectItem value="external">External (client)</SelectItem>
                      </SelectContent>
                    </Select></div>
                </div>
              )}
            </div>

            {/* Two-column layout: line editor on left, preview on right.
                Nov 2026 · Raised the breakpoint from `xl` (1280px) to
                `2xl` (1536px) so common 1366/1440 laptops get the
                table full-width instead of squeezed next to the 340px
                summary panel — avoids the horizontal scroll that
                users on sub-1600 displays were hitting on large
                packages. */}
            <div className="grid grid-cols-1 2xl:grid-cols-[minmax(0,1fr)_340px] gap-4">
              <div className="space-y-3 min-w-0">
                {/* Compact Add + Save toolbar */}
                <div className="flex gap-2 flex-wrap items-center border border-border bg-muted/20 p-2" data-testid="pkg-add-toolbar">
                  <ActivityMasterCombobox items={activityTypes} onPick={addFromMaster} />
                  <Button variant="outline" size="sm" className="rounded-none h-8 text-xs" onClick={addCustom} data-testid="pkg-add-custom"><Plus className="w-3 h-3 mr-1" /> Custom line</Button>
                  <Button variant="outline" size="sm" className="rounded-none h-8 text-xs" onClick={addSection} data-testid="pkg-add-section"><Layers className="w-3 h-3 mr-1" /> Add section</Button>
                  <Button variant="outline" size="sm" className="rounded-none h-8 text-xs" onClick={() => setPresetsOpen(true)} data-testid="pkg-open-presets"><FileStack className="w-3 h-3 mr-1" /> Presets</Button>
                  <Button variant="outline" size="sm" className="rounded-none h-8 text-xs" onClick={() => setBulkReassignOpen(true)} data-testid="pkg-open-bulk-reassign" title="Swap the assignee across every skill row with a given designation"><Shuffle className="w-3 h-3 mr-1" /> Bulk reassign</Button>
                  <Button variant="outline" size="sm" className="rounded-none h-8 text-xs" onClick={() => setSyncOpen(true)} data-testid="pkg-sync-to-calendar" title="Compare with the Content Calendar and create any missing items"><CalendarRange className="w-3 h-3 mr-1" /> Sync to Content Calendar</Button>
                  {shareToken ? (
                    <Button variant="outline" size="sm" className="rounded-none h-8 text-xs" onClick={copyShareLink} data-testid="pkg-share-copy" title="Copy shareable preview link"><Link2 className="w-3 h-3 mr-1" /> Copy share link</Button>
                  ) : (
                    <Button variant="outline" size="sm" className="rounded-none h-8 text-xs" onClick={generateShareLink} disabled={shareBusy} data-testid="pkg-share-new" title="Create a public preview link"><Share2 className="w-3 h-3 mr-1" /> Share link</Button>
                  )}
                  {shareToken && (
                    <button type="button" onClick={revokeShareLink} className="text-[10px] text-muted-foreground hover:text-destructive underline underline-offset-2" data-testid="pkg-share-revoke" title="Revoke this share link">
                      revoke
                    </button>
                  )}
                  <span className="text-[10px] text-muted-foreground ml-auto">{lines.length} lines · {sections.length} section{sections.length === 1 ? "" : "s"}</span>
                  <Button className="rounded-none h-8 text-xs" size="sm" onClick={saveAll} disabled={busy || !dirty} data-testid="pkg-save-all">
                    <Save className="w-3 h-3 mr-1" />{busy ? "Saving…" : dirty ? "Save changes" : "Saved"}
                  </Button>
                </div>

                {/* R80 · Drag-to-Group suggestion pill. */}
                {dragSuggestion && (
                  <div className="flex items-center gap-2 border border-amber-300 bg-amber-50 px-3 py-1.5" data-testid="pkg-drag-suggestion">
                    <Sparkles className="w-3.5 h-3.5 text-amber-700" />
                    <span className="text-[11px] text-amber-900">
                      {dragSuggestion.count} <strong>{dragSuggestion.cat}</strong> rows are ungrouped — want to bundle them into a section?
                    </span>
                    <Button size="sm" variant="outline" className="ml-auto h-7 text-[11px] rounded-none border-amber-400" onClick={acceptDragSuggestion} data-testid="pkg-drag-suggestion-accept">
                      Create &quot;{dragSuggestion.cat}&quot; section
                    </Button>
                  </div>
                )}

                {/* R77/R78 · Excel-like tabular editor with DnD reorder,
                     section grouping, skill roll-up, and effort warnings. */}
                <PackageLinesTable
                  lines={lines}
                  sections={sections}
                  currency={pkg.currency}
                  designations={designations}
                  activityTypes={activityTypes}
                  categories={mergedCategories}
                  members={members}
                  effortTolerancePct={tolerancePct}
                  showMoney={financialLensOn}
                  showRates={canRates}
                  showCosts={canCosts}
                  onUpdate={updateLine}
                  onRemove={removeLine}
                  onReorder={reorderLines}
                  onPrimaryAssigneeChanged={handlePrimaryAssigneeChanged}
                  onSectionsChange={updateSections}
                  onSkillHoursBackfill={(lineId, designation, hours) =>
                    setSkillBackfill({ line_id: lineId, designation, hours })}
                />

                {/* R78 · Package notes (rich text) — shown in the editor,
                     preview panel, and print/PDF surface. */}
                <div className="border border-border" data-testid="pkg-notes-card">
                  <div className="px-3 py-2 border-b border-border bg-muted/40 label-eyebrow text-[10px] flex items-center gap-2">
                    <span>Package notes</span>
                    <span className="text-muted-foreground normal-case tracking-normal font-normal">Shown on client-facing preview and print / PDF.</span>
                  </div>
                  <div className="p-3">
                    <SmartRichText
                      value={notesHtml}
                      onChange={(html) => { setNotesHtml(html); markDirty(); }}
                      placeholder="Anything the client should see with this package — scope, exclusions, delivery terms…"
                      testid="pkg-notes-editor"
                      minRows={6}
                      allowAiImprove={false}
                    />
                  </div>
                </div>

                {/* Status log */}
                {(pkg.status_log || []).length > 0 && (
                  <div className="border border-border" data-testid="pkg-status-log">
                    <div className="px-3 py-2 border-b border-border bg-muted/40 label-eyebrow text-[10px]">Status history</div>
                    {(pkg.status_log || []).slice().reverse().map((s, i) => (
                      <div key={i} className="px-3 py-1.5 border-b border-border last:border-0 text-xs flex gap-3">
                        <span className="font-mono text-muted-foreground">{(s.at || "").slice(0, 16).replace("T", " ")}</span>
                        <Badge variant="outline" className={`rounded-none text-[10px] ${PKG_STATUS_TONES[s.status] || ""}`}>{s.status}</Badge>
                        {s.comment && <span className="text-muted-foreground truncate">{s.comment}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Right-side Compact Preview */}
              <PackagePreviewCard
                pkg={pkg}
                wsId={activeWorkspaceId}
                lines={lines}
                sections={sections}
                notesHtml={notesHtml}
                clientName={clientName}
                brandName={brandName}
                startDate={meta.start_date}
                endDate={meta.end_date}
                approval={approval}
                shareUrl={shareUrl}
                grouped={previewGrouped}
                onToggleGrouped={() => setPreviewGroupedPersist(!previewGrouped)}
              />
            </div>
          </TabsContent>

          {showFinancials && (
            <TabsContent value="financials" className="pt-4 space-y-4" data-testid="pkg-financials">
              {/* Money summary cards, gated per-permission */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="pkg-finance-totals">
                {canRates && <TotalCard label="Monthly price" value={fmtMoney(t.monthly_price, pkg.currency)} testid="pkg-total-price" />}
                {canRates && <TotalCard label={`Net (after ${t.discount_pct || 0}% disc.)`} value={fmtMoney(t.net_monthly_price, pkg.currency)} testid="pkg-total-net" />}
                {canCosts && <TotalCard label="Internal cost" value={fmtMoney(t.monthly_cost, pkg.currency)} testid="pkg-total-cost" />}
                {canMargins && <TotalCard label="Margin" value={`${t.margin_pct ?? 0}%`} tone={t.margin_pct < 0 ? "text-red-600" : t.margin_pct < 20 ? "text-amber-600" : "text-emerald-700"} testid="pkg-total-margin" />}
                <TotalCard label="Monthly effort" value={`${t.monthly_hours ?? 0}h`} testid="pkg-total-hours-fin" />
              </div>
              {/* Discount editor — only for users who can see rates */}
              {canRates && (
                <div className="border border-border p-3 grid grid-cols-1 md:grid-cols-3 gap-3 items-end" data-testid="pkg-finance-editor">
                  <div><Label className="label-eyebrow mb-1 inline-block">Discount %</Label>
                    <Input type="number" min={0} max={100} value={meta.discount_pct} onChange={(e) => { setMeta((s) => ({ ...s, discount_pct: e.target.value })); markDirty(); }} className="h-9 rounded-none" data-testid="pkg-finance-discount" /></div>
                  <div className="md:col-span-2 text-xs text-muted-foreground">Rates come from the package&apos;s rate card. Deliverable rates can be tweaked on the Delivery tab.</div>
                </div>
              )}
              {/* Per-line financial table — only visible if user has ANY of rates/costs perms */}
              {(canRates || canCosts) && (
                <div className="border border-border" data-testid="pkg-finance-lines">
                  <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                    <div className="col-span-5">Deliverable</div>
                    <div className="col-span-2 text-right">Qty / mo</div>
                    {canRates && <div className="col-span-2 text-right">Price / mo</div>}
                    {canCosts && <div className={`${canRates ? "col-span-2" : "col-span-4"} text-right`}>Cost / mo</div>}
                    {canMargins && canRates && canCosts && <div className="col-span-1 text-right">Margin</div>}
                  </div>
                  {lines.map((l, i) => {
                    const mp = Number(l.monthly_price || 0);
                    const mc = Number(l.monthly_cost || 0);
                    const mm = mp ? Math.round((mp - mc) / mp * 100) : null;
                    return (
                      <div key={l.id || i} className="grid grid-cols-12 gap-2 px-3 py-1.5 border-t border-border text-xs items-center">
                        <div className="col-span-5 truncate">{l.name}</div>
                        <div className="col-span-2 text-right font-mono">{l.monthly_qty ?? "—"}</div>
                        {canRates && <div className="col-span-2 text-right font-mono text-emerald-700">{fmtMoney(mp, pkg.currency)}</div>}
                        {canCosts && <div className={`${canRates ? "col-span-2" : "col-span-4"} text-right font-mono`}>{fmtMoney(mc, pkg.currency)}</div>}
                        {canMargins && canRates && canCosts && <div className={`col-span-1 text-right font-mono ${mm != null && mm < 0 ? "text-red-600" : "text-emerald-700"}`}>{mm != null ? `${mm}%` : "—"}</div>}
                      </div>
                    );
                  })}
                </div>
              )}
              <PackageMonthlyBreakdown packageId={pid} currency={pkg?.currency} />
            </TabsContent>
          )}

          <TabsContent value="activity" className="pt-4">
            <ActivityTab entityType="package" entityId={pid} testid="pkg-activity" />
          </TabsContent>
        </Tabs>
        {pkg && <AuditFooter record={pkg} testid="pkg-audit-footer" />}
      </div>
      {/* R78 · Master presets dialog. */}
      <ActivityPresetsDialog
        open={presetsOpen}
        onOpenChange={setPresetsOpen}
        wsId={activeWorkspaceId}
        currentLines={lines}
        currentSections={sections}
        onApply={applyPreset}
        canManage={true}
      />
      {/* R99 · Bulk reassign designation. Applies patches through
           updateLine so each affected line is dirty-flagged; user still
           presses Save to persist. */}
      <BulkReassignDialog
        open={bulkReassignOpen}
        onClose={() => setBulkReassignOpen(false)}
        lines={lines}
        members={members}
        designations={designations}
        onApply={(patches) => {
          patches.forEach((p) => updateLine(p.index, { skill_breakdown: p.skill_breakdown }));
          toast.success(`Reassigned ${patches.reduce((n, p) => n + (p.skill_breakdown || []).length, 0)} skill row${patches.length === 1 ? "" : "s"}. Save the package to persist.`);
        }}
      />
      <PackageCalendarSyncDialog
        open={syncOpen}
        onClose={() => setSyncOpen(false)}
        workspaceId={activeWorkspaceId}
        packageId={pid}
        packageName={pkg?.name}
      />

      {/* R268vvv · Feb 2026 — Primary-assignee backfill prompt. Opens
          the moment a PM changes a line's owner dropdown; skipping
          simply keeps the new owner for future generations without
          touching existing rows. */}
      <Dialog open={!!backfill} onOpenChange={(o) => !o && setBackfill(null)}>
        <DialogContent className="max-w-lg rounded-none" data-testid="pkg-backfill-dialog">
          <DialogHeader>
            <DialogTitle>Push primary assignee to existing deliverables?</DialogTitle>
            <DialogDescription>
              New content items generated from this line will use the new owner automatically.
              To also update deliverables already generated on the calendar, pick a month range and confirm the impacted list below.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs bg-muted/40 border border-border px-3 py-2 font-mono">
              <div><span className="text-muted-foreground">Line:</span> {backfill?.line?.name || backfill?.line?.title || "—"}</div>
              <div><span className="text-muted-foreground">New owner:</span> {(members || []).find((m) => (m.user_id || m.id) === backfill?.next)?.name || (backfill?.next ? backfill.next : "— none —")}</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="label-eyebrow block mb-1">From month</span>
                <Input type="month" value={backfillFrom} onChange={(e) => setBackfillFrom(e.target.value)} className="rounded-none" data-testid="pkg-backfill-from" />
              </label>
              <label className="block">
                <span className="label-eyebrow block mb-1">To month</span>
                <Input type="month" value={backfillTo} onChange={(e) => setBackfillTo(e.target.value)} className="rounded-none" data-testid="pkg-backfill-to" />
              </label>
            </div>
            {/* R268bbbb · Feb 2026 — Preview panel. Splits items into
                "Will change" (their current owner differs from the new
                owner) and "Already correct". Every "Will change" row
                is toggle-able; unticking a row adds its id to
                `backfillExcluded` so the batch call skips it. */}
            {(() => {
              const items = backfillPreview.items || [];
              const newOwnerId = backfill?.next || null;
              const willChange = items.filter((i) => (i.current_owner_id || null) !== newOwnerId);
              const alreadyCorrect = items.length - willChange.length;
              const newOwnerName = (members || []).find((m) => (m.user_id || m.id) === newOwnerId)?.name || (newOwnerId ? newOwnerId : "— none —");
              return (
                <div className="border border-border" data-testid="pkg-backfill-preview">
                  <div className="px-2 py-1.5 bg-muted/30 border-b border-border flex items-center justify-between text-[10px] uppercase tracking-widest text-muted-foreground">
                    <span>
                      Preview · {backfillPreview.loading ? "loading…" : `${willChange.length} will change`}
                      {alreadyCorrect > 0 && <span className="text-muted-foreground/70"> · {alreadyCorrect} already correct</span>}
                    </span>
                    {willChange.length > 0 && (
                      <button
                        type="button"
                        className="text-primary hover:underline normal-case tracking-normal font-mono"
                        onClick={() => {
                          if (backfillExcluded.size >= willChange.length) setBackfillExcluded(new Set());
                          else setBackfillExcluded(new Set(willChange.map((i) => i.id)));
                        }}
                        data-testid="pkg-backfill-preview-toggle-all"
                      >
                        {backfillExcluded.size >= willChange.length ? "Include all" : "Exclude all"}
                      </button>
                    )}
                  </div>
                  <div className="max-h-56 overflow-auto divide-y divide-border/60">
                    {items.length === 0 && !backfillPreview.loading && (
                      <div className="text-[11px] text-muted-foreground italic px-3 py-3 text-center">
                        No existing deliverables in this range — only future generations will use the new owner.
                      </div>
                    )}
                    {willChange.map((i) => {
                      const excluded = backfillExcluded.has(i.id);
                      return (
                        <label key={i.id}
                               className="flex items-start gap-2 px-2 py-1.5 text-[11px] cursor-pointer hover:bg-muted/20"
                               data-testid={`pkg-backfill-preview-row-${i.id}`}>
                          <input
                            type="checkbox"
                            className="mt-0.5"
                            checked={!excluded}
                            onChange={(e) => {
                              const n = new Set(backfillExcluded);
                              if (e.target.checked) n.delete(i.id); else n.add(i.id);
                              setBackfillExcluded(n);
                            }}
                            data-testid={`pkg-backfill-preview-check-${i.id}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{i.title}</div>
                            <div className="text-muted-foreground text-[10px] flex flex-wrap gap-x-2">
                              <span>{i.month}</span>
                              {i.planned_date && <span>· {i.planned_date}</span>}
                              <span>·</span>
                              <span className="italic">
                                {i.current_owner_name || "unassigned"}
                                <span className="text-foreground/50 not-italic">  →  </span>
                                <span className="text-foreground not-italic font-medium">{newOwnerName}</span>
                              </span>
                            </div>
                          </div>
                        </label>
                      );
                    })}
                    {alreadyCorrect > 0 && (
                      <div className="text-[10px] text-muted-foreground/70 italic px-2 py-1 bg-muted/10">
                        · {alreadyCorrect} deliverable{alreadyCorrect === 1 ? "" : "s"} already owned by {newOwnerName} — skipped
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setBackfill(null)} data-testid="pkg-backfill-skip">
              Skip (future only)
            </Button>
            <Button className="rounded-none" onClick={runBackfill}
                    disabled={backfillBusy || !backfillFrom || !backfillTo || backfillFrom > backfillTo ||
                              // R268bbbb · disable if nothing selected to change
                              (backfillPreview.items || []).filter((i) => (i.current_owner_id || null) !== (backfill?.next || null))
                                .every((i) => backfillExcluded.has(i.id))}
                    data-testid="pkg-backfill-run">
              {backfillBusy ? "Applying…"
                : (() => {
                    const willChange = (backfillPreview.items || []).filter((i) => (i.current_owner_id || null) !== (backfill?.next || null));
                    const n = willChange.length - backfillExcluded.size;
                    return `Apply to ${n} item${n === 1 ? "" : "s"}`;
                  })()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feb 2026 · Skill-hours → tasks backfill. */}
      {skillBackfill && (
        <SkillHoursBackfillDialog
          wsId={activeWorkspaceId}
          pid={pid}
          lineId={skillBackfill.line_id}
          designation={skillBackfill.designation}
          hours={skillBackfill.hours}
          onClose={() => setSkillBackfill(null)}
        />
      )}
    </>
  );
}

/* ──────────────────────────────────────────────────────────────────
   Feb 2026 · Push a new skill-breakdown hours value out to every
   task spawned from this (package, line, designation) in a month
   range. The PM can preview the exact tasks that will change and
   deselect individual tasks.
   ────────────────────────────────────────────────────────────────── */
function SkillHoursBackfillDialog({ wsId, pid, lineId, designation, hours, onClose }) {
  const defaultFrom = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; })();
  const defaultTo = (() => {
    const d = new Date(); d.setMonth(d.getMonth() + 3);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  })();
  const [fromMonth, setFromMonth] = useState(defaultFrom);
  const [toMonth, setToMonth] = useState(defaultTo);
  const [preview, setPreview] = useState({ tasks: [], loading: false });
  const [excluded, setExcluded] = useState(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!wsId || !pid || !lineId || !designation) return;
    if (!fromMonth || !toMonth || fromMonth > toMonth) {
      setPreview({ tasks: [], loading: false });
      return;
    }
    let cancelled = false;
    setPreview((p) => ({ tasks: p.tasks || [], loading: true }));
    api.get(`/workspaces/${wsId}/packages/${pid}/skill-hours-backfill/preview`, {
      params: { line_id: lineId, designation, from_month: fromMonth, to_month: toMonth },
    }).then((r) => {
      if (cancelled) return;
      setPreview({ tasks: r.data?.tasks || [], loading: false });
      setExcluded(new Set());
    }).catch(() => {
      if (!cancelled) setPreview({ tasks: [], loading: false });
    });
    return () => { cancelled = true; };
  }, [wsId, pid, lineId, designation, fromMonth, toMonth]);

  const willChange = preview.tasks.filter((t) => Number(t.est_hours || 0) !== Number(hours));
  const alreadyCorrect = preview.tasks.length - willChange.length;

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/packages/${pid}/skill-hours-backfill`,
        {
          line_id: lineId,
          designation,
          hours: Number(hours) || 0,
          from_month: fromMonth,
          to_month: toMonth,
          excluded_task_ids: [...excluded],
        },
      );
      toast.success(`Updated ${data.modified} task${data.modified === 1 ? "" : "s"} to ${hours}h`);
      onClose();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Backfill failed"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-none max-h-[90vh] overflow-y-auto" data-testid="pkg-skill-hours-backfill-dialog">
        <DialogHeader>
          <DialogTitle>Push {hours}h to existing tasks?</DialogTitle>
          <DialogDescription>
            New tasks spawned from this skill row will use the new hours automatically.
            To update tasks already created on the calendar, pick a month range and confirm the list below.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs bg-muted/40 border border-border px-3 py-2 font-mono">
            <div><span className="text-muted-foreground">Designation:</span> {designation}</div>
            <div><span className="text-muted-foreground">New hours:</span> {hours}h / unit</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="label-eyebrow block mb-1">From month</span>
              <Input type="month" value={fromMonth} onChange={(e) => setFromMonth(e.target.value)}
                     className="rounded-none" data-testid="pkg-skill-backfill-from" />
            </label>
            <label className="block">
              <span className="label-eyebrow block mb-1">To month</span>
              <Input type="month" value={toMonth} onChange={(e) => setToMonth(e.target.value)}
                     className="rounded-none" data-testid="pkg-skill-backfill-to" />
            </label>
          </div>
          <div className="border border-border" data-testid="pkg-skill-backfill-preview">
            <div className="px-2 py-1.5 bg-muted/30 border-b border-border flex items-center justify-between text-[10px] uppercase tracking-widest text-muted-foreground">
              <span>
                Preview · {preview.loading ? "loading…" : `${willChange.length} will change`}
                {alreadyCorrect > 0 && <span className="text-muted-foreground/70"> · {alreadyCorrect} already {hours}h</span>}
              </span>
              {willChange.length > 0 && (
                <button type="button"
                        className="text-primary hover:underline normal-case tracking-normal font-mono"
                        onClick={() => {
                          if (excluded.size >= willChange.length) setExcluded(new Set());
                          else setExcluded(new Set(willChange.map((t) => t.id)));
                        }}
                        data-testid="pkg-skill-backfill-toggle-all">
                  {excluded.size >= willChange.length ? "Include all" : "Exclude all"}
                </button>
              )}
            </div>
            <div className="max-h-56 overflow-auto divide-y divide-border/60">
              {preview.tasks.length === 0 && !preview.loading && (
                <div className="text-[11px] text-muted-foreground italic px-3 py-3 text-center">
                  No tasks in this range — only future generations will use {hours}h.
                </div>
              )}
              {willChange.map((t) => {
                const off = excluded.has(t.id);
                return (
                  <label key={t.id} className="flex items-start gap-2 px-2 py-1.5 text-[11px] cursor-pointer hover:bg-muted/20"
                         data-testid={`pkg-skill-backfill-row-${t.id}`}>
                    <input type="checkbox" className="mt-0.5" checked={!off}
                           onChange={(e) => {
                             const n = new Set(excluded);
                             if (e.target.checked) n.delete(t.id); else n.add(t.id);
                             setExcluded(n);
                           }} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{t.title}</div>
                      <div className="text-muted-foreground text-[10px]">
                        {t.due_date} · <span className="italic">{t.est_hours || 0}h</span>
                        <span className="text-foreground/50 not-italic"> → </span>
                        <span className="text-foreground not-italic font-medium">{hours}h</span>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}
                  data-testid="pkg-skill-backfill-skip">
            Skip (future only)
          </Button>
          <Button onClick={confirm} disabled={busy}
                  className="rounded-none" data-testid="pkg-skill-backfill-confirm">
            {busy ? "Updating…" : `Update ${willChange.length - excluded.size} task${willChange.length - excluded.size === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TotalCard({ label, value, tone = "", testid }) {
  return (
    <Card className="rounded-none" data-testid={testid}>
      <CardContent className="p-3">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className={`text-xl font-bold leading-none mt-1 font-mono ${tone}`}>{value}</div>
      </CardContent>
    </Card>
  );
}


function PackagePreviewCard({ pkg, wsId, lines, sections, notesHtml, clientName, brandName, startDate, endDate, approval, shareUrl, grouped = true, onToggleGrouped }) {
  // R78 · Group lines into sections; within each section, subgroup by
  // category so the client-facing preview reads as a clean, logical outline.
  const sortedSections = [...(sections || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  const bySectionId = new Map();
  bySectionId.set(null, []);
  sortedSections.forEach((s) => bySectionId.set(s.id, []));
  (lines || []).forEach((l, i) => {
    const sid = l.section_id && bySectionId.has(l.section_id) ? l.section_id : null;
    bySectionId.get(sid).push({ ...l, _originalIdx: i });
  });
  for (const arr of bySectionId.values()) {
    arr.sort((a, b) => (Number(a.sort_order ?? a._originalIdx * 10) - Number(b.sort_order ?? b._originalIdx * 10)));
  }
  const groupByCategory = (arr) => {
    const buckets = new Map();
    for (const l of arr) {
      const cat = l.category || "other";
      if (!buckets.has(cat)) buckets.set(cat, []);
      buckets.get(cat).push(l);
    }
    return Array.from(buckets.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  };

  const previewSections = [];
  if (grouped) {
    sortedSections.forEach((s) => {
      const items = bySectionId.get(s.id) || [];
      if (items.length) previewSections.push({ id: s.id, name: s.name, color: s.color, groups: groupByCategory(items) });
    });
    const ungrouped = bySectionId.get(null) || [];
    if (ungrouped.length) {
      previewSections.push({
        id: null,
        name: sortedSections.length > 0 ? "Other" : null,
        color: null,
        groups: groupByCategory(ungrouped),
      });
    }
  } else {
    // R83 · Flat mode — a single unnamed section with one merged bucket
    // preserving the caller's sort_order. No section/category headers.
    const flat = [];
    sortedSections.forEach((s) => { flat.push(...(bySectionId.get(s.id) || [])); });
    flat.push(...(bySectionId.get(null) || []));
    if (flat.length) previewSections.push({ id: null, name: null, color: null, groups: [["", flat]] });
  }

  const totalCount = (lines || []).reduce((s, l) => s + (Number(l.count) || 0), 0);

  // R80 · Approval chip styling
  const apStatus = approval?.approval_status || "draft";
  const apStyle = {
    approved: { bg: "#dcfce7", fg: "#166534", border: "#86efac" },
    rejected: { bg: "#ffe4e6", fg: "#9f1239", border: "#fda4af" },
    pending:  { bg: "#fef3c7", fg: "#92400e", border: "#fcd34d" },
    draft:    { bg: "#f4f4f5", fg: "#52525b", border: "#d4d4d8" },
  }[apStatus] || { bg: "#f4f4f5", fg: "#52525b", border: "#d4d4d8" };

  const copyTable = async () => {
    // Rich, proposal-style copy. TSV for spreadsheet paste + HTML for docs/email.
    const dateRange = `${startDate || "—"} → ${endDate || "no end date"}`;
    const tsvRows = [
      `${clientName ? clientName + " · " : ""}${brandName ? brandName + " · " : ""}${pkg.name}`,
      `Period: ${dateRange}`,
      `Approval: ${apStatus}${approval?.approval_date ? ` (${approval.approval_date})` : ""}${approval?.approver_name ? ` — ${approval.approver_name}` : ""}${approval?.approver_type ? ` [${approval.approver_type}]` : ""}`,
      "",
    ];
    const htmlParts = [
      `<div style="font-family:Arial,sans-serif">`,
      `<div style="font-size:11px;text-transform:uppercase;letter-spacing:.12em;color:#666;">${escapeHTML(clientName || "")}${clientName && brandName ? " · " : ""}${escapeHTML(brandName || "")}</div>`,
      `<h2 style="margin:2px 0 4px;font-size:20px;">${escapeHTML(pkg.name || "")}</h2>`,
      `<div style="font-size:12px;color:#333;">Period: <strong>${escapeHTML(dateRange)}</strong></div>`,
      `<div style="font-size:12px;color:#333;margin-bottom:8px;">Approval: <span style="background:${apStyle.bg};color:${apStyle.fg};border:1px solid ${apStyle.border};padding:1px 6px;text-transform:uppercase;letter-spacing:.08em;font-size:10px;">${escapeHTML(apStatus)}</span>${approval?.approval_date ? ` · ${escapeHTML(approval.approval_date)}` : ""}${approval?.approver_name ? ` · ${escapeHTML(approval.approver_name)}` : ""}${approval?.approver_type ? ` <em>(${escapeHTML(approval.approver_type)})</em>` : ""}</div>`,
    ];
    previewSections.forEach((sec) => {
      const accent = sec.color || "#3B82F6";
      if (sec.name) {
        tsvRows.push("", `▸ ${sec.name}`);
        htmlParts.push(`<h4 style="margin:12px 0 4px;color:${accent};border-left:3px solid ${accent};padding-left:8px;">${escapeHTML(sec.name)}</h4>`);
      }
      sec.groups.forEach(([cat, items]) => {
        tsvRows.push(`  ${cat}`);
        tsvRows.push(`Item\tCount\tFrequency\tChannels`);
        htmlParts.push(`<div style="font-size:11px;text-transform:uppercase;letter-spacing:.08em;color:#666;margin-top:6px;">${escapeHTML(cat)}</div>`);
        htmlParts.push(`<table border="1" cellpadding="6" cellspacing="0" style="border-collapse:collapse;font-family:Arial,sans-serif;font-size:12px;width:100%;margin-bottom:6px;">`);
        htmlParts.push(`<thead><tr style="background:#f4f4f5"><th align="left">Item</th><th align="right">Count</th><th align="left">Frequency</th><th align="left">Channels</th></tr></thead><tbody>`);
        items.forEach((l) => {
          const freq = FREQ_LABELS[l.frequency] || l.frequency || "—";
          const chText = channelsToPlainText(l.channels);
          const chHtml = (l.channels || []).map((k) => {
            const c = getChannel(k);
            if (!c) return "";
            return `<span style="display:inline-block;background:${c.color}22;color:${c.color};border:1px solid ${c.color};padding:0 4px;margin-right:3px;font-size:10px;text-transform:uppercase;letter-spacing:.05em;">${escapeHTML(c.label)}</span>`;
          }).join(" ");
          tsvRows.push(`${l.name}\t${l.count}\t${freq}\t${chText}`);
          htmlParts.push(`<tr><td>${escapeHTML(l.name)}</td><td align="right">${l.count}</td><td>${escapeHTML(freq)}</td><td>${chHtml || "—"}</td></tr>`);
        });
        htmlParts.push(`</tbody></table>`);
      });
    });
    if (notesHtml) {
      htmlParts.push(`<h4 style="margin:12px 0 4px;font-family:Arial,sans-serif;">Notes</h4>`);
      htmlParts.push(`<div style="font-family:Arial,sans-serif;font-size:12px;">${notesHtml}</div>`);
    }
    htmlParts.push(`</div>`);
    const tsv = tsvRows.join("\n");
    const html = htmlParts.join("");
    try {
      if (window.ClipboardItem && navigator.clipboard?.write) {
        await navigator.clipboard.write([
          new window.ClipboardItem({
            "text/plain": new Blob([tsv], { type: "text/plain" }),
            "text/html": new Blob([html], { type: "text/html" }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(tsv);
      }
      toast.success("Package copied — paste into Docs, Sheets, or an email");
    } catch (e) {
      toast.error("Copy failed — " + (e?.message || "clipboard blocked"));
    }
  };

  const doPrint = () => window.print();

  return (
    <div className="border-2 border-primary/40 bg-primary/5 sticky top-4 self-start h-fit" data-testid="pkg-preview-card">
      <div className="px-3 py-2 border-b-2 border-primary/40 bg-primary/10 flex items-center gap-1 flex-wrap">
        <ListChecks className="w-3.5 h-3.5 text-primary" />
        <span className="label-eyebrow text-[10px] flex-1 min-w-0 truncate">Package preview</span>
        {onToggleGrouped && (
          <Button
            size="sm"
            variant="outline"
            className="rounded-none h-7 text-[11px] border-primary/50 no-print"
            onClick={onToggleGrouped}
            title={grouped ? "Currently grouped by section/category — click to flatten" : "Currently flat — click to group by section/category"}
            data-testid="pkg-preview-group-toggle"
          >
            {grouped ? "Grouped" : "Flat"}
          </Button>
        )}
        <Button size="sm" variant="outline" className="rounded-none h-7 text-[11px] border-primary/50" onClick={copyTable} data-testid="pkg-preview-copy">
          <ClipboardCopy className="w-3 h-3 mr-1" /> Copy
        </Button>
        <Button size="sm" variant="outline" className="rounded-none h-7 text-[11px] border-primary/50" onClick={doPrint} data-testid="pkg-preview-print">
          Print
        </Button>
        {/* R268cccc · Feb 2026 — Send Preview to client via email
            (branded PDF attachment). Only offered when we know the
            workspace + package id — never on unsaved drafts. */}
        {wsId && pkg?.id && (
          <SendPreviewButton wsId={wsId} pkg={pkg} clientName={clientName} brandName={brandName} />
        )}
      </div>
      {/* R80 · Rich header — client · brand · package name · dates · approval */}
      <div className="px-3 pt-2 pb-1 border-b border-border" data-testid="pkg-preview-header">
        {(clientName || brandName) && (
          <div className="text-[9px] uppercase tracking-widest text-muted-foreground">
            {clientName}{clientName && brandName ? " · " : ""}{brandName}
          </div>
        )}
        <div className="text-sm font-bold text-primary leading-tight" data-testid="pkg-preview-title">{pkg?.name || "Untitled"}</div>
        <div className="text-[10px] text-muted-foreground mt-0.5">
          Period: <span className="font-mono">{startDate || "—"} → {endDate || "no end"}</span>
        </div>
        <div className="mt-1 flex items-center gap-1 flex-wrap">
          <span
            className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border"
            style={{ background: apStyle.bg, color: apStyle.fg, borderColor: apStyle.border }}
            data-testid="pkg-preview-approval"
          >{apStatus}</span>
          {approval?.approval_date && <span className="text-[10px] text-muted-foreground">{approval.approval_date}</span>}
          {approval?.approver_name && <span className="text-[10px]">· {approval.approver_name}</span>}
          {approval?.approver_type && <span className="text-[9px] text-muted-foreground uppercase">({approval.approver_type})</span>}
        </div>
        {shareUrl && (
          <div className="mt-1 text-[10px] text-muted-foreground truncate" title={shareUrl} data-testid="pkg-preview-share-url">
            Share: <a href={shareUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline break-all">{shareUrl.replace(/^https?:\/\//, "")}</a>
          </div>
        )}
      </div>
      <div className="p-2 space-y-3 max-h-[70vh] overflow-y-auto">
        {previewSections.length === 0 && (
          <div className="px-2 py-4 text-center text-[11px] text-muted-foreground italic">No items yet</div>
        )}
        {previewSections.map((sec) => {
          const accent = sec.color || "hsl(var(--primary))";
          return (
            <div key={sec.id || "none"} data-testid={`pkg-preview-section-${sec.id || "none"}`}>
              {sec.name && (
                <div
                  className="mb-1 pb-0.5 text-[10px] uppercase tracking-widest font-bold border-b-2"
                  style={{ color: accent, borderColor: accent }}
                >{sec.name}</div>
              )}
              {sec.groups.map(([cat, items]) => (
                <div key={cat} className="mb-1.5" data-testid={`pkg-preview-cat-${cat}`}>
                  <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold mt-1">{cat}</div>
                  {/* R268bbbb · Feb 2026 — Fixed column widths + a
                      `table-fixed` layout so the Count, Frequency,
                      and Channels columns land at the same X
                      position across every category group in a
                      grouped preview. Previously each <table> auto-
                      sized based on its own content, so a longer
                      item name in one group would push its freq
                      column out of line with the next group. */}
                  <table className="w-full text-[11px] border-collapse table-fixed" data-testid={`pkg-preview-cat-table-${cat}`}>
                    <colgroup>
                      <col />
                      <col style={{ width: "32px" }} />
                      <col style={{ width: "68px" }} />
                      <col style={{ width: "56px" }} />
                    </colgroup>
                    <tbody>
                      {items.map((l, i) => (
                        <tr key={i} className="border-t border-border">
                          <td className="px-1 py-0.5 truncate" title={l.name}>{l.name}</td>
                          <td className="px-1 py-0.5 text-right font-mono">{Number(l.count) || 0}</td>
                          <td className="px-1 py-0.5 text-[10px] truncate">{FREQ_LABELS[l.frequency] || l.frequency || "—"}</td>
                          <td className="px-1 py-0.5">
                            <ChannelIcons channels={l.channels} size={11} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          );
        })}
        {(lines || []).length > 0 && (
          <div className="pt-2 mt-1 border-t-2 border-border text-[11px] flex items-center gap-2">
            <span className="label-eyebrow text-[10px]">Total</span>
            <span className="font-mono font-semibold ml-auto">{totalCount}</span>
            <span className="text-[10px] text-muted-foreground">{(lines || []).length} lines</span>
          </div>
        )}
        {notesHtml && (
          <div className="pt-2 mt-1 border-t border-border" data-testid="pkg-preview-notes">
            <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Notes</div>
            <div className="text-[11px] max-w-none">
              <SmartRichTextDisplay html={notesHtml} className="prose-sm" />
            </div>
          </div>
        )}
        {/* Feb 2026 · Effort rollups — Category + Designation.
            Computed purely from the current `lines` so they stay
            in sync with inline edits (no round-trip to backend). */}
        <PackageEffortRollups lines={lines || []} />
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
   Feb 2026 · Effort rollups shown under the Package preview on the
   edit screen. Two tables: by Category + by Designation. Monthly
   effort = effort_hours × qty_per_month, so a 4-reel/month line at
   2h/reel contributes 8h to its category + each skill's hours ×
   qty_per_month contribute to that designation.
   ────────────────────────────────────────────────────────────────── */
function PackageEffortRollups({ lines }) {
  const roll = (() => {
    const byCat = {};
    const byDesig = {};
    let total = 0;
    for (const ln of lines) {
      const qty = Number(ln.qty_per_month ?? ln.quantity ?? ln.count ?? 1) || 1;
      const hoursPerUnit = Number(ln.effort_hours) || 0;
      const lineMonthly = hoursPerUnit * qty;
      total += lineMonthly;
      const cat = (ln.category || "Uncategorised").trim() || "Uncategorised";
      byCat[cat] = (byCat[cat] || 0) + lineMonthly;
      const skills = Array.isArray(ln.skill_breakdown) ? ln.skill_breakdown : [];
      if (skills.length === 0) {
        byDesig["Unassigned designation"] = (byDesig["Unassigned designation"] || 0) + lineMonthly;
      } else {
        for (const s of skills) {
          const d = (s.designation || "").trim() || "Unassigned designation";
          const h = (Number(s.hours) || 0) * qty;
          byDesig[d] = (byDesig[d] || 0) + h;
        }
      }
    }
    const catRows = Object.entries(byCat).sort((a, b) => b[1] - a[1]);
    const desigRows = Object.entries(byDesig).sort((a, b) => b[1] - a[1]);
    return { catRows, desigRows, total };
  })();
  if (roll.catRows.length === 0 && roll.desigRows.length === 0) return null;
  const Table = ({ title, rows, testid }) => (
    <div className="border border-border" data-testid={testid}>
      <div className="px-2 py-1 bg-muted/40 border-b border-border text-[9px] uppercase tracking-widest font-semibold text-muted-foreground flex items-center justify-between">
        <span>{title}</span>
        <span className="font-mono normal-case tracking-normal">{roll.total.toFixed(1)}h / mo total</span>
      </div>
      <ul className="divide-y divide-border/60">
        {rows.map(([k, h]) => {
          const pct = roll.total > 0 ? Math.round((h / roll.total) * 100) : 0;
          return (
            <li key={k} className="px-2 py-1 text-[11px] flex items-center gap-2" data-testid={`${testid}-row-${k.replace(/\s+/g, "-")}`}>
              <span className="flex-1 min-w-0 truncate" title={k}>{k}</span>
              <span className="font-mono text-muted-foreground text-[10px]">{pct}%</span>
              <span className="font-mono font-semibold w-14 text-right">{h.toFixed(1)}h</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
  return (
    <div className="pt-2 mt-1 border-t border-border space-y-2" data-testid="pkg-preview-effort-rollups">
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold">Effort rollups</div>
      <Table title="By Category" rows={roll.catRows} testid="pkg-rollup-category" />
      <Table title="By Designation" rows={roll.desigRows} testid="pkg-rollup-designation" />
    </div>
  );
}

/**
 * SendPreviewButton — R268cccc · Feb 2026
 * Opens a dialog that (a) fetches the brand's approval/escalation
 * contacts, (b) lets the PM pick which recipients to include and
 * add ad-hoc email addresses, and (c) fires the send-email endpoint
 * which attaches a branded PDF rendered from the same lines/sections
 * shown in the Package Preview panel.
 */
function SendPreviewButton({ wsId, pkg, clientName, brandName }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggested, setSuggested] = useState([]); // [{email, name, role, source}]
  const [picked, setPicked] = useState({});       // email → bool
  const [manual, setManual] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [ccPm, setCcPm] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !wsId || !pkg?.id) return;
    setLoading(true);
    api.get(`/workspaces/${wsId}/packages/${pkg.id}/preview/recipients`)
      .then((r) => {
        const cs = r.data?.contacts || [];
        setSuggested(cs);
        // Pre-select every suggested contact so the happy path is a
        // single-click send.
        const initial = {};
        cs.forEach((c) => { initial[c.email] = true; });
        setPicked(initial);
      })
      .catch(() => setSuggested([]))
      .finally(() => setLoading(false));
    setSubject(`${pkg.name || "Package"} · Preview`);
    setMessage(
      `Hi,\n\nSharing the package preview for ${pkg.name || "this engagement"}. Please review the attached PDF and reply if anything needs tweaking.\n\nThanks,`,
    );
    setManual(""); setCcPm(true);
  }, [open, wsId, pkg?.id, pkg?.name]);

  const parsedManual = useMemo(() => (
    manual
      .split(/[\s,;]+/)
      .map((s) => (s || "").trim().toLowerCase())
      .filter((s) => s && s.includes("@"))
  ), [manual]);
  const recipients = useMemo(() => {
    const set = new Set();
    for (const c of suggested) if (picked[c.email]) set.add(c.email);
    for (const m of parsedManual) set.add(m);
    return [...set];
  }, [suggested, picked, parsedManual]);

  const send = async () => {
    if (recipients.length === 0) return;
    setSending(true);
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/packages/${pkg.id}/preview/send-email`,
        { recipients, subject, message, cc_pm: ccPm },
      );
      const cc = ccPm ? " (+ you cc'd)" : "";
      toast.success(`Sent to ${data.sent} recipient${data.sent === 1 ? "" : "s"}${cc}`);
      setOpen(false);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Send failed"));
    } finally { setSending(false); }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        className="rounded-none h-7 text-[11px] border-primary/50"
        onClick={() => setOpen(true)}
        data-testid="pkg-preview-send"
      >
        <Send className="w-3 h-3 mr-1" /> Send to client
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-none" data-testid="pkg-preview-send-dialog">
          <DialogHeader>
            <DialogTitle>Send package preview</DialogTitle>
            <DialogDescription>
              Emails the client a branded PDF of the preview panel — same sections + items shown on the right.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {(clientName || brandName) && (
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">
                {clientName}{clientName && brandName ? " · " : ""}{brandName}
              </div>
            )}
            <div>
              <div className="label-eyebrow block mb-1">Suggested recipients</div>
              {loading && <div className="text-[11px] text-muted-foreground italic">Loading contacts…</div>}
              {!loading && suggested.length === 0 && (
                <div className="text-[11px] text-muted-foreground italic border border-dashed border-border p-2">
                  No approval/escalation contacts on the linked brand. Add emails manually below.
                </div>
              )}
              {suggested.length > 0 && (
                <div className="border border-border divide-y divide-border/60 max-h-40 overflow-auto">
                  {suggested.map((c) => (
                    <label key={c.email}
                           className="flex items-center gap-2 px-2 py-1 text-xs cursor-pointer hover:bg-muted/30"
                           data-testid={`pkg-send-row-${c.email}`}>
                      <input type="checkbox"
                             checked={!!picked[c.email]}
                             onChange={(e) => setPicked((p) => ({ ...p, [c.email]: e.target.checked }))}
                             data-testid={`pkg-send-check-${c.email}`} />
                      <span className="font-medium truncate flex-1">{c.name || c.email}</span>
                      <span className="text-muted-foreground text-[10px] truncate">{c.email}</span>
                      {c.role && <span className="text-[9px] uppercase tracking-widest text-muted-foreground/70">{c.role}</span>}
                    </label>
                  ))}
                </div>
              )}
            </div>
            <label className="block">
              <span className="label-eyebrow block mb-1">Also send to (comma-separated)</span>
              <Input
                type="text"
                value={manual}
                onChange={(e) => setManual(e.target.value)}
                placeholder="alice@client.com, bob@client.com"
                className="rounded-none"
                data-testid="pkg-send-manual"
              />
              {parsedManual.length > 0 && (
                <span className="text-[10px] text-muted-foreground">
                  + {parsedManual.length} manual · total {recipients.length}
                </span>
              )}
            </label>
            <label className="block">
              <span className="label-eyebrow block mb-1">Subject</span>
              <Input type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
                     className="rounded-none" data-testid="pkg-send-subject" />
            </label>
            <label className="block">
              <span className="label-eyebrow block mb-1">Message</span>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)}
                         rows={5}
                         className="w-full text-xs border border-border bg-transparent p-2 rounded-none focus:outline-none focus:border-primary"
                         data-testid="pkg-send-message" />
            </label>
            <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={ccPm} onChange={(e) => setCcPm(e.target.checked)}
                     data-testid="pkg-send-cc-pm" />
              CC me on this send
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-none"
                    disabled={sending || recipients.length === 0}
                    onClick={send}
                    data-testid="pkg-send-run">
              {sending ? "Sending…" : `Send to ${recipients.length || 0} recipient${recipients.length === 1 ? "" : "s"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}



function escapeHTML(s) {
  return String(s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}
